import { createContext, ReactNode, useContext, useEffect, useState } from 'react';

export type ThemeId = 'claro' | 'sepia' | 'oscuro' | 'wms';

export const THEMES: { id: ThemeId; label: string; description: string }[] = [
  { id: 'claro', label: 'Claro', description: 'Tema original de la plataforma' },
  { id: 'sepia', label: 'Sepia', description: 'Tonos cálidos, ideal para lectura prolongada' },
  { id: 'oscuro', label: 'Oscuro', description: 'Descansado para los ojos, uso nocturno' },
  { id: 'wms', label: 'WMS', description: 'Estilo industrial de bodega, azul sobre fondo oscuro' },
];

const STORAGE_KEY = 'lcc.theme';
const DEFAULT_THEME: ThemeId = 'claro';

interface ThemeContextValue {
  theme: ThemeId;
  setTheme: (theme: ThemeId) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function isValidTheme(value: string | null): value is ThemeId {
  return !!value && THEMES.some((t) => t.id === value);
}

/**
 * Aplica el tema visual global mediante el atributo data-theme en <html>
 * (ver shared/theme/globals.css, que define las variables CSS por tema).
 * index.html ya trae un script inline que aplica el tema guardado ANTES del
 * primer render, para evitar el parpadeo de "tema equivocado" al cargar.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeId>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return isValidTheme(saved) ? saved : DEFAULT_THEME;
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  return <ThemeContext.Provider value={{ theme, setTheme: setThemeState }}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme debe usarse dentro de <ThemeProvider>');
  return ctx;
}
